/* eslint-disable max-lines -- single- and multi-select converged here; the rows, chips, footer and Select All logic, the separable pieces, are already their own files */
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import * as React from 'react';

import {
  type DimeComboboxChipAction,
  DimeCombobox,
  DimeComboboxCollection,
  DimeComboboxContent,
  DimeComboboxEmpty,
  DimeComboboxGroup,
  DimeComboboxInput,
  DimeComboboxLabel,
  DimeComboboxList,
  useDimeComboboxAnchor,
} from '../DimeCombobox.js';
import { DimePickerEmpty } from '../DimePickerEmpty.js';
import { useAutoFocusOnFill } from '../hooks/use-auto-focus-on-fill.js';
import type { DimeAutocompleteFooterAction } from '../lib/dime-autocomplete-footer-action.js';
import type { DimeAutocompleteOption } from '../lib/dime-autocomplete-option.js';
import type { DimeEmptyState } from '../lib/dime-empty-state.js';
import { FieldRequiredContext } from '../lib/field-required-context.js';
import {
  groupAutocompleteOptions,
  type DimeAutocompleteOptionGroup,
} from '../lib/group-autocomplete-options.js';
import { isAllSelected } from '../lib/is-all-selected.js';
import { isSameOptionSelection } from '../lib/is-same-option-selection.js';
import { multipleSelectionSummary } from '../lib/multiple-selection-summary.js';
import { resolveSelectAll } from '../lib/resolve-select-all.js';
import { SELECT_ALL_OPTION } from '../lib/select-all-option.js';
import { toggleSelectAll } from '../lib/toggle-select-all.js';
import { TOUCH_LAYOUT_CLASSES } from '../lib/touch-layout-classes.js';
import { cn } from '../lib/utils.js';
import { withSelectAllOption } from '../lib/with-select-all-option.js';
import { DimeAutocompleteChips } from './DimeAutocompleteChips.js';
import { DimeAutocompleteFooter } from './DimeAutocompleteFooter.js';
import { DimeAutocompleteOptionRow } from './DimeAutocompleteOptionRow.js';

const TYPED_INPUT_REASONS = new Set(['input-change', 'input-clear', 'clear-press']);

function optionLabel(item: unknown): string {
  return (item as DimeAutocompleteOption).label;
}

type DimeAutocompleteSharedProps = {
  options: DimeAutocompleteOption[];
  id: string;
  placeholder: string;
  emptyMessage: string;
  onBlur?: () => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onOpenChange?: (open: boolean) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  /** Caps what can be typed into the search, such as a lot number's length. */
  maxLength?: number;
  disabled?: boolean;
  /** Not with `chips`, where each chip removes its own pick. */
  showClear?: boolean;
  /** Highlights the first match as the user types, so Enter picks it. */
  autoHighlight?: boolean;
  'aria-invalid'?: boolean;
  /** Defaults to the enclosing `DimeField`'s `required`. */
  'aria-required'?: boolean;
  'aria-describedby'?: string;
  'aria-busy'?: boolean;
  /** For a field with no visible label of its own, such as one cell of an editable table row. */
  'aria-label'?: string;
  className?: string;
  /** `touch` is a 44px target, for a picker on a tablet or phone. */
  size?: 'default' | 'touch';
  /** Focuses and opens this field once when it becomes true; caller is responsible for flipping it back after the advance. */
  autoFocusOnFill?: boolean;
  onInputValueChange?: (value: string) => void;
  /** For options searched server-side: receives the typed text ('' once typing ends), and `options` are shown as given, not filtered again by label. */
  onSearchChange?: (text: string) => void;
  /** Labels for picks `options` may not hold (an earlier search page, a deactivated record); a pick in neither is dropped. */
  selectedOptions?: ReadonlyArray<DimeAutocompleteOption>;
  /** Shown under the options, such as why the list is cut short. */
  notice?: React.ReactNode;
  /** A last row that acts rather than picks, such as "+ New Contact"; the popup closes first, and it must be passed from the first render, as one added later neither takes Tab nor closes the popup. */
  footerAction?: DimeAutocompleteFooterAction;
  /** Shown in place of `emptyMessage` while there's nothing to pick at all, its action in the footer's place; like `footerAction`, pass any action from the first render. */
  emptyState?: DimeEmptyState;
};

type DimeAutocompleteSingleProps = DimeAutocompleteSharedProps & {
  multiple?: false;
  value: string;
  onValueChange: (value: string) => void;
  /** When set, prepended as a selectable empty value (replaces native select's blank option). */
  emptyOptionLabel?: string;
  /**
   * When true, a non-empty `value` with no matching option is reset to `''` once `options`
   * has loaded. Only for callers whose `options` list is the complete catalog. Paginated or
   * filtered lists, and fields that inject a current-but-excluded option, must leave this
   * off — a truncated page looks identical to a deleted FK and would wipe a valid id.
   */
  clearWhenMissing?: boolean;
  /** Selects the lone enabled option while the value is empty and the user has not typed a search. */
  autoSelectSingleOption?: boolean;
  /** For a field that can change but never go empty: no Clear, and text erased and left puts the pick's name back. */
  restoreOnErase?: boolean;
};

/** Multi-select mode, where a caller whose `options` is a partial page names its picks in `selectedOptions`, since a pick in neither is dropped from the next `onValueChange`. */
type DimeAutocompleteMultipleProps = DimeAutocompleteSharedProps & {
  multiple: true;
  value: string[];
  onValueChange: (value: string[]) => void;
  /** Shows the picks as removable chips ahead of the input, rather than as the input's text. */
  chips?: boolean;
  /** On by default: a Select All row that picks every listed option, or unpicks them while all are picked. */
  selectAll?: boolean;
  /** A cap on how many values the field accepts; Select All is offered only while the picks it would leave, off-list ones included, fit. */
  maxPicks?: number;
  /** The accessible name of a chip's remove button, given the pick's label; "Remove <label>" by default. */
  chipRemoveLabel?: (label: string) => string;
  /** A button of the chip's own, such as marking that person on site. */
  chipAction?: (option: DimeAutocompleteOption) => DimeComboboxChipAction | undefined;
  /** A short muted note after the chip's label, such as "Owner". */
  chipBadge?: (option: DimeAutocompleteOption) => string | undefined;
};

export type DimeAutocompleteProps = Readonly<
  DimeAutocompleteSingleProps | DimeAutocompleteMultipleProps
>;

/** The picker for one value or several, in a form or anywhere else (a list-toolbar filter uses `DimeFilterCombobox`). */
export function DimeAutocomplete(props: DimeAutocompleteProps): React.JSX.Element {
  const {
    id,
    placeholder,
    emptyMessage,
    options,
    onBlur,
    onFocus,
    onOpenChange,
    inputRef,
    maxLength,
    disabled,
    showClear = false,
    autoHighlight,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
    'aria-describedby': ariaDescribedBy,
    'aria-busy': ariaBusy,
    'aria-label': ariaLabel,
    className,
    size = 'default',
    autoFocusOnFill,
    onInputValueChange,
    onSearchChange,
    selectedOptions,
    notice,
    footerAction,
    emptyState,
  } = props;
  const multiple = props.multiple === true;
  const chips = props.multiple === true && props.chips === true;
  const emptyOptionLabel = multiple ? undefined : props.emptyOptionLabel;
  const clearWhenMissing = multiple ? false : props.clearWhenMissing === true;
  const autoSelectSingleOption = multiple ? false : props.autoSelectSingleOption === true;
  const restoreOnErase = props.multiple !== true && props.restoreOnErase === true;
  const value = props.value;
  const onValueChange = props.onValueChange as (next: string | string[]) => void;

  const anchorRef = useDimeComboboxAnchor();
  // A field that fills its lone option itself never auto-opens: closing a popup mid-open leaves it stuck in the page.
  const fillsLoneOption =
    autoSelectSingleOption && options.length === 1 && options[0]?.disabled !== true;
  const [open, setOpen] = useAutoFocusOnFill(
    id,
    autoFocusOnFill === true && disabled !== true && !fillsLoneOption,
  );
  // WHY: controlled-ness is decided once at mount, from autoFocusOnFill, footerAction and an empty-state action — flipping mid-life makes Base UI ignore the first `open={true}`, and keeping every caller controlled trips act() warnings in callers that never asked for it.
  const [isControlledOpen] = React.useState(
    autoFocusOnFill !== undefined || footerAction !== undefined || emptyState?.action !== undefined,
  );
  const handleOpenChange = (next: boolean) => {
    if (isControlledOpen) setOpen(next);
    onOpenChange?.(next);
  };
  const openProps = isControlledOpen
    ? { open, onOpenChange: handleOpenChange }
    : onOpenChange === undefined
      ? {}
      : { onOpenChange: handleOpenChange };
  const items = React.useMemo(
    () =>
      emptyOptionLabel == null ? options : [{ value: '', label: emptyOptionLabel }, ...options],
    [emptyOptionLabel, options],
  );
  const groupedItems = React.useMemo(
    () => (multiple ? null : groupAutocompleteOptions(items)),
    [multiple, items],
  );
  const comboboxItems = groupedItems ?? items;
  const filter = ComboboxPrimitive.useFilter({ multiple });
  const [query, setQuery] = React.useState('');
  const optionByValue = React.useMemo(
    () => new Map([...(selectedOptions ?? []), ...items].map((option) => [option.value, option])),
    [selectedOptions, items],
  );
  const resolvedSingle = multiple ? undefined : optionByValue.get(value as string);
  const singleValue = resolvedSingle?.value;
  const singleLabel = resolvedSingle?.label;
  const singleDisabled = resolvedSingle?.disabled;
  // Rebuilt from primitives: a reloaded options list re-creates the same option, and a new selection object makes Base UI reset the text being typed.
  const stableSingle = React.useMemo<DimeAutocompleteOption | null>(
    () =>
      singleValue === undefined || singleLabel === undefined
        ? null
        : { value: singleValue, label: singleLabel, disabled: singleDisabled },
    [singleValue, singleLabel, singleDisabled],
  );
  const selected = React.useMemo(() => {
    if (multiple) {
      return (value as string[])
        .map((each) => optionByValue.get(each))
        .filter((option): option is DimeAutocompleteOption => option != null);
    }
    return stableSingle;
  }, [multiple, optionByValue, value, stableSingle]);

  // Base UI rewrites the input to the selected label whenever the value's identity changes, which would wipe a search in progress each time a new options page arrives.
  const [stableSelected, setStableSelected] = React.useState(selected);
  const selectionUnchanged = isSameOptionSelection(stableSelected, selected);
  if (!selectionUnchanged) setStableSelected(selected);
  const shownSelection = selectionUnchanged ? stableSelected : selected;

  // A server-searched list is one page, so Select All would pick only the people or parts it happens to hold.
  const selectAll =
    props.multiple === true &&
    props.selectAll !== false &&
    props.onSearchChange == null &&
    (props.maxPicks === undefined ||
      toggleSelectAll(options, value as string[]).length <= props.maxPicks);
  const allSelected = selectAll && isAllSelected(options, value as string[]);
  // Select All reads as ticked while everything is, so it sits in the value alongside the picks.
  const comboboxValue = React.useMemo(
    () =>
      allSelected
        ? [...(shownSelection as DimeAutocompleteOption[]), SELECT_ALL_OPTION]
        : shownSelection,
    [allSelected, shownSelection],
  );
  // A multi-select filters for itself, and lists Select All only while nothing is typed, as it would pick rows the search hides.
  const filteredItems = React.useMemo(() => {
    if (!multiple) return onSearchChange == null ? undefined : comboboxItems;
    const matches =
      onSearchChange != null || query.trim() === ''
        ? items
        : items.filter((option) => filter.contains(option, query, optionLabel));
    return selectAll && query.trim() === '' ? withSelectAllOption(matches, options) : matches;
  }, [multiple, onSearchChange, items, comboboxItems, query, filter, options, selectAll]);
  const searchProps = filteredItems === undefined ? {} : { filteredItems };
  // A server-searched list empties on a search that misses, which is no reason to explain an empty catalog.
  const showEmptyState =
    emptyState != null && items.length === 0 && (onSearchChange == null || query.trim() === '');
  const shownFooterAction = showEmptyState ? (emptyState.action ?? footerAction) : footerAction;

  // Latest-ref: callers often pass an inline arrow, a fresh reference every render, so the
  // correction effect below reads this instead of depending on the callback identity.
  const onValueChangeRef = React.useRef(onValueChange);
  React.useLayoutEffect(() => {
    onValueChangeRef.current = onValueChange;
  });

  // Opt-in: a non-empty value with no matching item would otherwise render blank while the
  // form still holds the stale id — looks empty/required, but save keeps the old FK. Only
  // callers whose `options` is the full catalog should set `clearWhenMissing`; a paginated
  // page is indistinguishable from a deleted row. Gated on `options.length > 0` so a still-
  // loading catalog cannot wipe a valid saved value.
  React.useEffect(() => {
    if (clearWhenMissing && options.length > 0 && value !== '' && selected === null) {
      onValueChangeRef.current('');
    }
  }, [clearWhenMissing, options, value, selected]);

  const [searchTerm, setSearchTerm] = React.useState('');
  const valueKey = multiple ? (value as string[]).join('\u0000') : (value as string);
  const [searchedValueKey, setSearchedValueKey] = React.useState(valueKey);
  if (searchedValueKey !== valueKey) {
    setSearchedValueKey(valueKey);
    setSearchTerm('');
  }

  const onInputValueChangeRef = React.useRef(onInputValueChange);
  React.useLayoutEffect(() => {
    onInputValueChangeRef.current = onInputValueChange;
  });
  const reportedSearchRef = React.useRef('');
  React.useEffect(() => {
    if (reportedSearchRef.current === '') return;
    reportedSearchRef.current = '';
    onInputValueChangeRef.current?.('');
  }, [valueKey]);

  const optionsKey = React.useMemo(
    () => options.map((option) => option.value).join('\u0000'),
    [options],
  );
  const clearedOptionsKeyRef = React.useRef<string | null>(null);
  const narrowedOptionsRef = React.useRef<DimeAutocompleteOption[] | null>(null);
  React.useEffect(() => {
    if (searchTerm !== '') {
      narrowedOptionsRef.current = options;
      return;
    }
    if (options === narrowedOptionsRef.current) return;
    if (optionsKey === clearedOptionsKeyRef.current) return;
    const [onlyOption] = options;
    if (!autoSelectSingleOption || disabled || value !== '' || options.length !== 1) return;
    if (onlyOption.disabled) return;
    onValueChangeRef.current(onlyOption.value);
    setOpen(false);
  }, [autoSelectSingleOption, disabled, options, optionsKey, value, searchTerm, setOpen]);

  // Base UI only re-syncs the input's text when the popup closes, so erased text is held here until the field is left.
  const [erasingText, setErasingText] = React.useState<string | null>(null);
  const restoreProps = restoreOnErase
    ? { inputValue: erasingText ?? stableSingle?.label ?? '' }
    : {};

  const multipleSummary = React.useMemo(
    () =>
      multiple
        ? multipleSelectionSummary(selected as DimeAutocompleteOption[], placeholder)
        : undefined,
    [multiple, selected, placeholder],
  );
  const fieldRequired = React.useContext(FieldRequiredContext);
  const required = ariaRequired ?? (fieldRequired || undefined);
  // Chips show the picks on screen, but the field still describes them, so a screen reader hears them on the field itself.
  const hasMultipleSelection = multiple && (selected as DimeAutocompleteOption[]).length > 0;
  const chipProps =
    props.multiple === true
      ? {
          chipRemoveLabel: props.chipRemoveLabel,
          chipAction: props.chipAction,
          chipBadge: props.chipBadge,
        }
      : {};

  // WHY: `placeholder` is name-of-last-resort, so with a label present the selection never
  // reaches the accessibility tree -- expose it as a description instead, appended to any the
  // caller passed.
  const summaryDescriptionId = `${id}-selection`;
  const describedBy = hasMultipleSelection
    ? [ariaDescribedBy, summaryDescriptionId].filter(Boolean).join(' ')
    : ariaDescribedBy;
  const footerActionRef = React.useRef<HTMLButtonElement>(null);
  // The action sits under the list, out of its arrow keys, so Tab moves into it rather than out of the field, which would close the list.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Tab' || event.shiftKey || !open || footerActionRef.current == null) return;
    event.preventDefault();
    // A sheet or dialog's focus trap would otherwise take the same Tab and wrap focus to its top.
    event.stopPropagation();
    footerActionRef.current.focus();
  };
  const handleBlur = () => {
    if (restoreOnErase) setErasingText(null);
    onBlur?.();
  };

  return (
    <DimeCombobox
      multiple={multiple}
      items={comboboxItems}
      value={comboboxValue}
      disabled={chips ? disabled : undefined}
      autoHighlight={autoHighlight}
      onValueChange={(next) => {
        if (multiple) {
          onValueChange(resolveSelectAll(next as DimeAutocompleteOption[], options, allSelected));
          return;
        }
        const nextValue = (next as DimeAutocompleteOption | null)?.value ?? '';
        if (restoreOnErase && nextValue === '') return;
        clearedOptionsKeyRef.current = next != null && nextValue === '' ? optionsKey : null;
        onValueChange(nextValue);
      }}
      itemToStringValue={(item) => (item as DimeAutocompleteOption).value}
      itemToStringLabel={optionLabel}
      isItemEqualToValue={(item, val) => {
        if (val == null) {
          return false;
        }
        return (item as DimeAutocompleteOption).value === (val as DimeAutocompleteOption).value;
      }}
      onInputValueChange={(next, details) => {
        setQuery(next);
        if (restoreOnErase) setErasingText(next);
        onSearchChange?.(details.reason === 'input-change' ? next : '');
        if (!TYPED_INPUT_REASONS.has(details.reason)) {
          if (!multiple && reportedSearchRef.current !== '') {
            setSearchTerm('');
            reportedSearchRef.current = '';
            onInputValueChange?.('');
          }
          return;
        }
        setSearchTerm(next);
        reportedSearchRef.current = next;
        onInputValueChange?.(next);
      }}
      {...openProps}
      {...searchProps}
      {...restoreProps}
    >
      {chips ? (
        <DimeAutocompleteChips
          anchorRef={anchorRef}
          id={id}
          selected={selected as DimeAutocompleteOption[]}
          placeholder={placeholder}
          {...chipProps}
          inputRef={inputRef}
          maxLength={maxLength}
          disabled={disabled}
          className={cn(className, size === 'touch' && 'min-h-11')}
          onBlur={handleBlur}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          aria-invalid={ariaInvalid}
          aria-required={required}
          aria-describedby={describedBy}
          aria-busy={ariaBusy}
          aria-label={ariaLabel}
        />
      ) : (
        <DimeComboboxInput
          id={id}
          ref={inputRef}
          maxLength={maxLength}
          placeholder={multipleSummary ?? placeholder}
          className={cn(className ?? 'w-full', size === 'touch' && TOUCH_LAYOUT_CLASSES.control)}
          inputClassName={hasMultipleSelection ? 'placeholder:text-foreground' : undefined}
          showClear={showClear && !restoreOnErase}
          disabled={disabled}
          onBlur={handleBlur}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          aria-invalid={ariaInvalid}
          aria-required={required}
          aria-describedby={describedBy}
          aria-busy={ariaBusy}
          aria-label={ariaLabel}
        />
      )}
      {hasMultipleSelection && (
        <span id={summaryDescriptionId} className="sr-only">
          {`Selected: ${multipleSummary}`}
        </span>
      )}
      <DimeComboboxContent anchor={chips ? anchorRef : undefined}>
        <DimeComboboxEmpty>
          {showEmptyState ? (
            <DimePickerEmpty title={emptyState.title} description={emptyState.description} />
          ) : (
            emptyMessage
          )}
        </DimeComboboxEmpty>
        <DimeComboboxList>
          {(item) => {
            if (groupedItems) {
              const group = item as DimeAutocompleteOptionGroup;
              return (
                <DimeComboboxGroup key={group.value || '__dime_ungrouped__'} items={group.items}>
                  {group.value && <DimeComboboxLabel>{group.value}</DimeComboboxLabel>}
                  <DimeComboboxCollection>
                    {(entry) => {
                      const option = entry as DimeAutocompleteOption;
                      return (
                        <DimeAutocompleteOptionRow
                          key={option.value !== '' ? option.value : '__dime_empty__'}
                          option={option}
                        />
                      );
                    }}
                  </DimeComboboxCollection>
                </DimeComboboxGroup>
              );
            }
            const option = item as DimeAutocompleteOption;
            return (
              <DimeAutocompleteOptionRow
                key={option.value !== '' ? option.value : '__dime_empty__'}
                option={option}
              />
            );
          }}
        </DimeComboboxList>
        <DimeAutocompleteFooter
          fieldId={id}
          notice={notice}
          action={shownFooterAction}
          actionRef={footerActionRef}
          onClose={() => handleOpenChange(false)}
        />
      </DimeComboboxContent>
    </DimeCombobox>
  );
}
